import React, { useState, useEffect } from 'react';
import api from '../api';

// --- MOBILE-FIRST STYLES ---
const THEME = {
    card: '#1e293b', 
    border: '#334155', 
    textSec: '#94a3b8', 
    primary: '#3b82f6', 
    success: '#10b981', 
    danger: '#ef4444',
    bg: '#0f172a'
};

const ProjectManager = () => {
    const [projects, setProjects] = useState([]);
    const [newProject, setNewProject] = useState({ name: '', location: '', status: 'ACTIVE' });
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        fetchProjects();
        // Simple listener to handle resize
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchProjects = async () => {
        try {
            const res = await api.get('config/projects/');
            setProjects(res.data);
        } catch (err) { console.error("Failed to load projects", err); } 
        finally { setLoading(false); }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        try {
            await api.post('config/projects/', newProject);
            setNewProject({ name: '', location: '', status: 'ACTIVE' });
            fetchProjects();
            alert("✅ Project Created");
        } catch (err) { alert("Error creating project"); }
    };

    const toggleStatus = async (id, currentStatus) => {
        try {
            await api.patch(`config/projects/${id}/`, { is_active: !currentStatus });
            fetchProjects();
        } catch (err) { alert("Failed to update status"); }
    };

    const styles = {
        container: {
            padding: isMobile ? '10px' : '20px',
            maxWidth: '1200px',
            margin: '0 auto'
        },
        // Form switches from Stack (mobile) to Grid (desktop)
        formCard: {
            background: THEME.card,
            padding: '20px',
            borderRadius: '12px',
            border: `1px solid ${THEME.border}`,
            marginBottom: '20px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '15px',
            alignItems: isMobile ? 'stretch' : 'end'
        },
        inputGroup: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        label: {
            color: THEME.textSec,
            fontSize: '0.85rem',
            fontWeight: 500
        },
        input: {
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '1rem', // Larger font for easier tapping
            background: '#020617',
            border: `1px solid ${THEME.border}`,
            color: 'white',
            boxSizing: 'border-box'
        },
        submitBtn: {
            padding: '12px 24px',
            background: THEME.primary,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600,
            height: isMobile ? 'auto' : '48px', // Match input height
            marginTop: isMobile ? '10px' : '0'
        },
        // Grid for Desktop Cards, Stack for Mobile
        gridContainer: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '15px'
        },
        projectCard: {
            background: THEME.card,
            borderRadius: '12px',
            border: `1px solid ${THEME.border}`,
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: '140px'
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '15px'
        },
        title: {
            margin: 0,
            fontSize: '1.1rem',
            color: 'white',
            fontWeight: 600
        },
        subText: {
            color: THEME.textSec,
            fontSize: '0.9rem',
            marginTop: '5px'
        },
        badge: (isActive) => ({
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: 700,
            background: isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(148, 163, 184, 0.1)',
            color: isActive ? THEME.success : THEME.textSec,
            border: `1px solid ${isActive ? THEME.success : THEME.border}`,
            display: 'inline-block'
        }),
        actionBtn: (isActive) => ({
            width: '100%',
            padding: '10px',
            borderRadius: '8px',
            border: `1px solid ${THEME.border}`,
            background: 'transparent',
            color: isActive ? THEME.danger : THEME.success,
            fontWeight: 500,
            cursor: 'pointer',
            marginTop: 'auto'
        })
    };

    if (loading) return <div style={{color:'white', padding:'20px'}}>Loading...</div>;

    return (
        <div className="animate-fade-in" style={styles.container}>
            
            {/* 1. CREATE FORM */}
            <div style={styles.formCard}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Project Name</label>
                    <input 
                        type="text" 
                        placeholder="e.g. Warehouse B" 
                        value={newProject.name} 
                        onChange={e => setNewProject({...newProject, name: e.target.value})} 
                        style={styles.input} 
                        required 
                    />
                </div>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Location / Grid Ref</label>
                    <input 
                        type="text" 
                        placeholder="e.g. Sector 7G" 
                        value={newProject.location} 
                        onChange={e => setNewProject({...newProject, location: e.target.value})} 
                        style={styles.input} 
                    />
                </div>
                <button onClick={handleAdd} style={styles.submitBtn}>
                    Create Project
                </button>
            </div>

            {/* 2. RESPONSIVE PROJECT LIST (Grid of Cards) */}
            <h3 style={{color:'white', margin:'0 0 15px 5px'}}>Your Projects</h3>
            
            <div style={styles.gridContainer}>
                {projects.map(p => (
                    <div key={p.id} style={styles.projectCard}>
                        <div style={styles.cardHeader}>
                            <div>
                                <h4 style={styles.title}>{p.name}</h4>
                                <div style={styles.subText}>📍 {p.location || 'No location'}</div>
                            </div>
                            <span style={styles.badge(p.is_active)}>
                                {p.is_active ? 'ACTIVE' : 'ARCHIVED'}
                            </span>
                        </div>

                        <button 
                            onClick={() => toggleStatus(p.id, p.is_active)}
                            style={styles.actionBtn(p.is_active)}
                        >
                            {p.is_active ? 'Archive Project' : 'Re-Activate Project'}
                        </button>
                    </div>
                ))}
            </div>

            {projects.length === 0 && (
                <div style={{color: THEME.textSec, textAlign: 'center', padding: '40px', background: THEME.card, borderRadius: '12px'}}>
                    No projects found. Create one above!
                </div>
            )}
        </div>
    );
};

export default ProjectManager;